<template>
  <div class="row">
    <div class="col-12 col-lg-12 mb-3">
      <label class="form-item__label info-popup">
        {{ $t("platform.settings.inpage.template") }}
        <svg
          v-svg
          v-tooltip="{
            content: $t('platform.settings.inpage.template_hint'),
            triggers: ['hover', 'click'],
          }"
          symbol="info"
          size="0 0 18 18"
          role="info"
        ></svg>
      </label>

      <custom-multiselect ref="inpageSelect" class="multiselect-wrapper multiselect-w-100" :value="in_page_templates" :disabled="enable_split_testing" :list="templateOptions" :searchBy="'label'" :treeSelect="true" :allowEmpty="false" :multiply="true">
        <template v-slot:block="props">
          <span v-if="props.value">{{ props.value.label || props.value }}</span>
        </template>
        <template v-slot:tree-parent="props">
          <span>{{ props.value.label }}</span>
        </template>
        <template v-slot:tree-child="props">
          <span>{{ props.value.label }}</span>
        </template>
      </custom-multiselect>

      <CheckBoxWrapper class="mt-3" :disabled="in_page_templates.length == 1" v-model="enable_split_testing" :text="$t('platform.settings.split_test_title')" />

      <div class="info-box mt-2 disabled">
        <svg v-svg symbol="info" size="0 0 18 18" role="info"></svg>
        <span v-html="$t('platform.settings.split_testing')"></span>
      </div>
    </div>

    <div class="col-12 mb-30">
      <label class="form-item__label info-popup">
        {{ $t("platform.settings.inpage.position") }}

        <svg
          v-svg
          v-tooltip="{
            content: $t('platform.settings.description.inpage.position'),
            triggers: ['hover', 'click'],
          }"
          symbol="info"
          size="0 0 18 18"
          role="info"
        ></svg>
      </label>
      <custom-multiselect class="multiselect-wrapper multiselect-w-100" v-model="position" :list="positionOptions" :allowEmpty="false">
        <template v-slot:block="props">
          <span>
            {{ props.value.value }}
          </span>
        </template>
        <template v-slot:list="props">
          <span>
            {{ props.value.value }}
          </span>
        </template>
      </custom-multiselect>
    </div>

    <div class="col-12 col-lg-6">
      <Counter v-model="delay" :disabled="true" :max="60" :placeholder="$t('sec') + '.'" :min="0" :title="'platform.settings.inpage.delay'" :description="'platform.settings.description.inpage.delay'" />
    </div>

    <div class="col-12 col-lg-6 space-for-sm">
      <Counter v-model="delay_after_click" :disabled="true" :max="600" :min="0" :placeholder="$t('sec') + '.'" :title="'platform.settings.inpage.pause_after_click'" :description="'platform.settings.description.inpage.pause_after_click'" />
    </div>
    <div class="col-12 col-lg-6">
      <Counter v-model="delay_after_close" :disabled="true" :max="600" :placeholder="$t('sec') + '.'" :min="0" :title="'platform.settings.inpage.pause_after_close'" :description="'platform.settings.description.inpage.pause_after_close'" />
    </div>

    <div class="col-12 col-lg-6 space-for-sm">
      <Counter v-model="close_button_delay" :disabled="true" :max="30" :min="0" :placeholder="$t('sec') + '.'" :title="'platform.settings.inpage.close_delay'" :description="'platform.settings.inpage.close_delay_hint'" />
    </div>
    <div class="col-12 col-lg-6">
      <Counter v-model="clicks_to_close_count" :disabled="true" :max="5" :min="1" :title="'platform.settings.inpage.close_clicks_amount'" :description="'platform.settings.inpage.close_clicks_hint'" />
    </div>
    <div class="col-12 col-lg-6">
      <Counter v-model="auto_refresh_interval" :disabled="true" :placeholder="$t('sec') + '.'" :max="120" :min="10" :title="'platform.settings.inpage.autorefresh_interval'" :description="'platform.settings.inpage.autorefresh_hint'" />
    </div>

    <div class="col-12">
      <CheckBoxWrapper class="mt-3" v-model="show_container" :disabled="true" :text="$t('platform.settings.inpage.show_container')" />
      <svg
        v-svg
        v-tooltip="{
          content: $t('platform.settings.inpage.show_container_hint'),
          triggers: ['hover', 'click'],
        }"
        style="fill: #5183cf"
        symbol="info"
        size="0 0 18 18"
        class="mb-2 ml-1"
      ></svg>
      <div class="form-item__field mt-2" :class="{ 'mb-0': $v.selector.valid }" v-if="show_container">
        <label for="selector" class="form-item__label">{{ $t("platform.settings.inpage.selector") }}</label>
        <input type="text" name="selector" :class="{ invalid: !$v.selector.valid && $v.selector.$dirty }" v-model.trim="selector" @blur="$v.selector.$touch" />
        <small v-if="!$v.selector.valid && $v.selector.$dirty" class="error mt-2">
          {{ $t("platform.settings.pop.invalid_selector") }}
        </small>
      </div>
    </div>
    <div class="col-12 mb-4">
      <CheckBoxWrapper class="mt-3" v-model="show_ad" :disabled="true" :text="$t('platform.settings.show_ad_preview')" />
    </div>
    <div class="col-12">
      <CheckBoxWrapper v-model="enable_onclick_backdrop" :disabled="true" :text="$t('platform.settings.enable_onclick_blackout')" />
      <svg
        v-svg
        v-tooltip="{
          content: $t('platform.settings.description.inpage.onclick_blackout'),
          triggers: ['hover', 'click'],
        }"
        style="fill: #5183cf"
        symbol="info"
        size="0 0 18 18"
        class="mb-2 ml-1"
      ></svg>
    </div>

    <div class="col-12 mt-4">
      <DelaySettings :delay="dimming" v-model="dimming" :disable="true" :min="0" :max="100" :variable="'%'" :title="'platform.settings.blackout_range'" :description="'platform.settings.description.native_web_push.blackout_settings'" class="pb-2" />
    </div>
  </div>
</template>

<script>
import CheckBoxWrapper from "@/components/CheckBoxWrapper.vue";
import Counter from "@/components/PlatformSettings/Counter.vue";
import DelaySettings from "@/components/PlatformSettings/DelaySettings.vue";

export default {
  name: "InPage",
  components: {
    CheckBoxWrapper,
    Counter,
    DelaySettings,
  },
  data() {
    return {
      enable_split_testing: false,
      show_container: false,
      enable_onclick_backdrop: false,
      selector: "",
      loading: true,
      delay: 0,
      show_ad: false,
      dimming: 0,
      close_button_delay: 0,
      clicks_to_close_count: 1,
      auto_refresh_interval: 30,
      templateOptions: [
        {
          id: 1,
          label: "Default",
        },
        {
          id: 100,
          label: "InPage Push",
          disable: true,
          children: [
            { id: -1, label: "OS", groupValue: true },
            { id: 101, label: "Mac" },
            { id: 102, label: "Windows" },
            { id: 103, label: "Android" },
            { id: 104, label: "iOS" },
            { id: -1, label: "Custom", groupValue: true },
            { id: 105, label: "Black" },
            { id: 106, label: "White" },
            { id: 107, label: "Pink(Dating)" },
            { id: 108, label: "White(Online)" },
            { id: -1, label: "Social", groupValue: true },
            { id: 109, label: "Facebook" },
            { id: 110, label: "Instagram" },
            { id: 111, label: "Telegram" },
            { id: 112, label: "Twitter" },
            { id: 113, label: "Whatsapp" },
            { id: -1, label: "Classic", groupValue: true },
            { id: 114, label: "Small" },
            { id: 115, label: "Dialog" },
            { id: 116, label: "Compact" },
            { id: 117, label: "Frame" },
          ],
        },
        {
          id: 200,
          label: "Alert Icons",
          disable: true,
          children: [
            { id: -1, label: "Basic bubble", groupValue: true },
            { id: 201, label: "Ripple effect" },
            { id: 202, label: "Ripple effect 2" },
            { id: 203, label: "Static display" },
            { id: -1, label: "Text Pulse", groupValue: true },
            { id: 204, label: "Spin Animation" },
            { id: 205, label: "Twist Notification" },
            { id: -1, label: "Dynamic Message", groupValue: true },
            { id: 206, label: "Pure white" },
            { id: 207, label: "Float Motion" },
            { id: 208, label: "Purple gradient" },
          ],
        },
        {
          id: 300,
          label: "Custom widgets",
          disable: true,
          children: [
            { id: -1, label: "Flat style", groupValue: true },
            { id: 301, label: "Light" },
            { id: 302, label: "Dark" },
            { id: -1, label: "Retro", groupValue: true },
            { id: 303, label: "IOS Dark" },
            { id: 304, label: "Large" },
            { id: 305, label: "Large 1" },
            { id: 306, label: "Large 2" },
            { id: 307, label: "Large 3" },
            { id: 308, label: "Deep" },
            { id: 309, label: "Grand" },
          ],
        },
      ],
      in_page_templates: [{ id: 0, label: "Default" }],
      delay_after_click: 10,
      delay_after_close: 10,
      position: { id: 1, value: "bottom-right" },
      positionOptions: [
        { id: 1, value: "top-left" },
        { id: 2, value: "top-right" },
        { id: 3, value: "bottom-left" },
        { id: 4, value: "bottom-right" },
      ],
      sell_missclick_as: { value: "OnClick" },
      miss_click_options: [{ value: "OnClick" }, { value: "InPage" }],
      open_type: { id: 1, title: "new_tab" },
      open_type_options: [
        { id: 1, title: "new_tab" },
        { id: 2, title: "new_tab_left" },
        { id: 4, title: "replace" },
        { id: 5, title: "popup" },
      ],
      displaySettings: {
        disableDeviceSell: [],
        disableOsSell: [],
        notShowReff: false,
        disableYandex: false,
      },
      osVersions: [],
    };
  },
  watch: {
    position() {
      this.$emit("setPosition", this.position.value);
    },
  },
};
</script>

<style lang="scss" scoped>
.info-box {
  border: 1px solid #eeeef3;
  border-radius: 10px;
  padding: 16px;
  font-size: 14px;
  display: flex;
  svg {
    fill: #5183cf;
    flex-shrink: 0;
    margin-right: 6px;
  }

  &.disabled {
    opacity: 0.5;
  }
}

.mb-30 {
  margin-bottom: 30px;
}
</style>
